// Master bus: resonator bank -> [dry + convolution reverb] -> compressor/limiter -> gain -> out.
// The reverb IR is generated procedurally (exponentially-decaying filtered noise) so no
// asset is needed. The generative `space` axis controls the wet mix.
import { mulberry32 } from '@shared/rng';

/** Build a synthetic "oceanic space" impulse response: decaying, slightly dark noise. */
export function makeReverbIR(ctx: BaseAudioContext, seconds = 4.5, decay = 3.2): AudioBuffer {
  const rate = ctx.sampleRate;
  const len = Math.max(1, Math.floor(seconds * rate));
  const ir = ctx.createBuffer(2, len, rate);
  const rng = mulberry32(0xea15);
  for (let ch = 0; ch < 2; ch++) {
    const data = ir.getChannelData(ch);
    let lp = 0;
    for (let i = 0; i < len; i++) {
      const t = i / len;
      const env = Math.pow(1 - t, decay);
      const white = rng() * 2 - 1;
      // one-pole low-pass to darken the tail (underwater feel)
      lp += 0.22 * (white - lp);
      data[i] = lp * env;
    }
  }
  return ir;
}

/** A tanh soft-clip curve: ~linear at low level, smoothly bounded to ±1 — a brickwall safety. */
function makeSoftClipCurve(n = 2048, drive = 1.5) {
  const curve = new Float32Array(n);
  const norm = Math.tanh(drive);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(drive * x) / norm;
  }
  return curve;
}

export class MasterChain {
  readonly input: GainNode; // bank connects here (makeup-gained)
  readonly ambientInput: GainNode; // ocean bed + ambient sound-bank layers (bypass makeup)
  readonly sampleInput: GainNode; // direct sampler one-shots (no makeup, but WITH reverb)
  private readonly dry: GainNode;
  private readonly wet: GainNode;
  private readonly convolver: ConvolverNode;
  private readonly comp: DynamicsCompressorNode;
  private readonly master: GainNode;
  private readonly softClip: WaveShaperNode;
  // metering (auditing the "drive"): a read-only tap on the pre-soft-clip bus
  private readonly meter: AnalyserNode;
  private readonly meterBuf: Float32Array<ArrayBuffer>;
  readonly makeupBase = 46; // reference single-voice makeup; App's leveler sets the live makeup
  // feedback delay (echo) bus, per-preset
  private readonly delaySend: GainNode;
  private readonly delay: DelayNode;
  private readonly delayFb: GainNode;
  private readonly delayWet: GainNode;

  constructor(ctx: AudioContext) {
    // The input node doubles as a makeup-gain stage: the modal bank's per-strike output
    // is intrinsically low-level (b0 = amp*(1-R), tiny for long decays), so we lift it here.
    // The bank's own internal limiter brickwalls dense passages before this stage.
    this.input = ctx.createGain();
    this.input.gain.value = this.makeupBase; // App's leveler overrides this each tick
    this.dry = ctx.createGain();
    this.wet = ctx.createGain();
    this.convolver = ctx.createConvolver();
    this.convolver.buffer = makeReverbIR(ctx);
    this.comp = ctx.createDynamicsCompressor();
    // Gentle bus glue (not a brickwall). The investigation found the old ratio-20 / -3 dB settings
    // were squashing dense passages by 10–17 dB = the "overdriven" character. A low ratio + soft
    // knee + lower threshold levels gently; the tanh soft-clip downstream is the true-peak safety.
    this.comp.threshold.value = -10;
    this.comp.knee.value = 12;
    this.comp.ratio.value = 3;
    this.comp.attack.value = 0.005;
    this.comp.release.value = 0.18;
    this.master = ctx.createGain();
    this.master.gain.value = 1.0;
    this.ambientInput = ctx.createGain();
    this.sampleInput = ctx.createGain();
    this.softClip = ctx.createWaveShaper();
    this.softClip.curve = makeSoftClipCurve();
    this.softClip.oversample = '2x';

    // routing: ... -> compressor -> master gain -> soft-clip safety -> destination
    this.input.connect(this.dry);
    this.input.connect(this.convolver);
    this.convolver.connect(this.wet);
    this.dry.connect(this.comp);
    this.wet.connect(this.comp);
    this.ambientInput.connect(this.comp); // ocean bed + ambient layers join the bus post-makeup
    // direct sampler one-shots join the SAME dry+reverb mix as the resonators (no makeup gain)
    this.sampleInput.connect(this.dry);
    this.sampleInput.connect(this.convolver);
    this.comp.connect(this.master);
    this.master.connect(this.softClip);
    this.softClip.connect(ctx.destination);
    // Read-only meter tap on the pre-soft-clip bus (peak here > 1.0 means the soft-clip is saturating).
    this.meter = ctx.createAnalyser();
    this.meter.fftSize = 1024;
    this.meterBuf = new Float32Array(this.meter.fftSize);
    this.master.connect(this.meter);

    // Echo bus: tap the compressor output -> delaySend -> delay (feedback loop) -> back into comp.
    this.delaySend = ctx.createGain();
    this.delaySend.gain.value = 0;
    this.delay = ctx.createDelay(2.0);
    this.delay.delayTime.value = 0.3;
    this.delayFb = ctx.createGain();
    this.delayFb.gain.value = 0.35;
    this.delayWet = ctx.createGain();
    this.delayWet.gain.value = 0.6; // trimmed echo return (see below)
    this.comp.connect(this.delaySend);
    this.delaySend.connect(this.delay);
    this.delay.connect(this.delayFb);
    this.delayFb.connect(this.delay); // feedback
    this.delay.connect(this.delayWet);
    this.delayWet.connect(this.master);

    this.setSpace(0.6);
  }

  /** space in [0,1] -> wet/dry balance (more space = wetter, slightly quieter dry). */
  setSpace(space: number): void {
    const s = Math.max(0, Math.min(1, space));
    this.wet.gain.value = 0.15 + 0.6 * s;
    this.dry.gain.value = 0.9 - 0.25 * s;
  }

  setMasterGain(g: number): void {
    this.master.gain.value = g;
  }

  /**
   * Set the makeup gain directly. The App runs a feed-forward LEVELER: it measures the actual
   * pre-makeup bus level and sets makeup = drive · min(BASE, CEILING / busPeak), so loud presets
   * (few-but-loud resonant voices, or many voices) are capped to a fixed output ceiling while sparse
   * presets keep their natural quiet — adapting to per-voice loudness AND polyphony, not just count.
   * `tau` is the smoothing time-constant (fast when reducing for a peak, slower when releasing).
   */
  setMakeup(g: number, tau = 0.04): void {
    this.input.gain.setTargetAtTime(Math.max(0, g), this.input.context.currentTime, tau);
  }

  /**
   * Auditing snapshot of how hard the master is being driven:
   *  - peak/rms: pre-soft-clip bus level (peak > 1 => the soft-clip is saturating)
   *  - reductionDb: live compressor gain reduction in dB (more negative = harder limiting)
   *  - clipping: true when the soft-clip is actively shaping the peak
   */
  getMeters(): { peak: number; rms: number; reductionDb: number; clipping: boolean } {
    this.meter.getFloatTimeDomainData(this.meterBuf);
    let peak = 0;
    let sumSq = 0;
    for (let i = 0; i < this.meterBuf.length; i++) {
      const v = this.meterBuf[i];
      const a = v < 0 ? -v : v;
      if (a > peak) peak = a;
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / this.meterBuf.length);
    return { peak, rms, reductionDb: this.comp.reduction, clipping: peak > 0.98 };
  }

  /** Per-preset echo: wet 0..WET_MAX, time seconds, feedback 0..0.6. wet=0 disables. */
  setDelay(wet = 0, timeSec = 0.3, feedback = 0.35): void {
    const t = this.delay.context.currentTime;
    // The echo return (delayWet) joins `master` POST-compressor and is NOT seen by the App's makeup
    // leveler — only the final soft-clip bounds it. Routing it through the compressor instead would
    // compound with the internal delay feedback (send taps comp output) and push the loop gain toward
    // unity (runaway tail), so we keep it post-comp and instead cap the SEND so a feedback-built tail
    // can't drive the soft-clip into audible saturation. Measured: at WET_MAX 0.7 the Abyssal tail
    // peaks ~0.85 (vs 0.95 at wet 1.0) — safely below the 0.98 clip point. (No preset exceeds 0.6.)
    const WET_MAX = 0.7;
    this.delaySend.gain.setTargetAtTime(Math.max(0, Math.min(WET_MAX, wet)), t, 0.1);
    this.delay.delayTime.setTargetAtTime(Math.max(0.02, Math.min(2, timeSec)), t, 0.1);
    this.delayFb.gain.setTargetAtTime(Math.max(0, Math.min(0.6, feedback)), t, 0.1);
  }
}
